import { computed } from "vue";
import { useRoute } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import type { AIAgentContext } from "@/services/ai_agent";
import type { Dashboard } from "@/services/dashboard";
import { useDataFiltersStore } from "@/store/data_filters";
import { useActiveDashboardTabStore } from "@/store/activeDashboardTab";

const DASHBOARD_ROUTES = new Set(["view-dashboard", "edit-dashboard"]);

/**
 * Build an AIAgentContext from the current route.
 * Automatically injects dashboardId when the user is viewing/editing a dashboard.
 */
export function useAIContext() {
  const route = useRoute();
  const queryClient = useQueryClient();
  const dataFiltersStore = useDataFiltersStore();
  const activeDashboardTab = useActiveDashboardTabStore();

  const context = computed<AIAgentContext>(() => {
    const ctx: AIAgentContext = {
      viewName: route.name as string | undefined,
      routePath: route.path,
    };

    if (dataFiltersStore.temporalFilter?.selection) {
      ctx.temporalFilter = dataFiltersStore.temporalFilter;
    }

    // Inject dashboard ID when on a dashboard route
    if (DASHBOARD_ROUTES.has(route.name as string)) {
      const pk = Number(route.params.pk);
      if (pk > 0) {
        ctx.dashboardId = pk;

        // The editor's own tab wins: it does not keep `?tab=` in step, so
        // the route would name whichever tab the dashboard was opened on.
        const editorTab = activeDashboardTab.active;
        if (route.name === "edit-dashboard" && editorTab?.pk === pk) {
          ctx.dashboardTab = { index: editorTab.index, name: editorTab.name };
          return ctx;
        }

        const tabIndex = Number(route.query.tab) || 0;
        // Coupled to useDashboardQuery's key structure: ["dashboard", { pk, ... }]
        // If that key shape changes, this predicate must be updated to match.
        const entries = queryClient.getQueriesData<Dashboard>({
          queryKey: ["dashboard"],
          predicate: (query) => {
            const params = query.queryKey[1];
            return (
              typeof params === "object" &&
              params !== null &&
              "pk" in params &&
              (params as { pk: unknown }).pk === pk
            );
          },
        });
        const dashboard = entries[0]?.[1];
        const tabName = dashboard?.tabs?.[tabIndex]?.name;
        if (tabName) {
          ctx.dashboardTab = { index: tabIndex, name: tabName };
        }
      }
    }

    return ctx;
  });

  return { context };
}
